import React, { useState } from 'react';
import {
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Chip,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import FlexService from '../services/FlexService';

const PHASE_COLORS = {
  DRAFT: 'default',
  FINALIZED: 'info',
  EXECUTING: 'warning',
  BLOCKED: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
};

// The plan's state in words: the tenant never sees the internal codes.
const PHASE_LABELS = {
  DRAFT: 'Draft',
  FINALIZED: 'Ready to build',
  EXECUTING: 'Building',
  BLOCKED: 'Waiting for an answer',
  COMPLETED: 'Built',
  FAILED: 'Stopped',
};

// FH-269: a warehouse design is built part by part (Areas, Locations, ...); get_plan_status sends each part's progress.
const BUILD_PHASE_LABELS = {
  NOT_STARTED: 'Not started',
  BUILDING: 'Building',
  BUILT: 'Built',
  FAILED: 'Stopped',
};

function buildPhaseIcon(status) {
  switch (status) {
    case 'BUILT':
      return stepIcon('DONE');
    case 'FAILED':
      return stepIcon('FAILED');
    case 'BUILDING':
      return <HourglassBottomIcon sx={{ color: '#3b82f6' }} fontSize="small" />;
    default:
      return stepIcon('PENDING');
  }
}

function recordsText(phase) {
  if (phase.status === 'NOT_STARTED') {
    return `${phase.records} to build`;
  }
  return `${phase.recordsBuilt} of ${phase.records} built`;
}

/**
 * Left-to-right step tracker for a plan's real progress (agent_plan /
 * agent_plan_item -- see feedback in project_generic_agent_planning_primitive).
 * Renders the same structured `planStatus` block plan_finalize/plan_execute/
 * get_plan_status embed in their result and the agent relays verbatim (per
 * structured_response_instructions.flex) -- this is the UI half of the "chat
 * stays short, the panel shows the real progress" split: the agent is told
 * never to recite step counts in prose once it emits this block.
 *
 * "Render on turn completion" scope (deliberately, for the first pass): this
 * shows the plan's state as of the end of the chat turn that produced it, not
 * live tick-by-tick progress while a long-running step is mid-execution --
 * matches how the Atom Response table pointer already works. True
 * mid-execution live progress (polling/push) is a scoped-out follow-up.
 *
 * Nested sub-plans (docs/PLAN_NESTING_AND_HUMAN_IN_LOOP_DESIGN.md section 2.4,
 * built 2026-09-29): a SUB_PLAN step's handler (plan_step_delegate_to_subplan)
 * always returns {childPlanKey, promoted, note, phase} in the step's own
 * `result.value` -- see db/40_flex_scripts/99_plan_subplan_scripts.sql. Any
 * step carrying a childPlanKey gets an expand affordance; expanding lazily
 * fetches that child's own planStatus via get_plan_status (the same script
 * plan_finalize/plan_execute already call, exposed directly here through
 * FlexService.executeScript) and renders it with this SAME component,
 * recursively -- depth is naturally bounded by how deep the backend actually
 * nested things (SYSTEM_CONFIG.MAX_PLAN_NESTING_DEPTH), not by anything the
 * UI enforces. Promoted sub-plans (split into their own root plan once
 * nesting would exceed that depth) are still previewable inline the same way
 * -- they are also independently visible wherever root plans are listed, per
 * the design doc, but nothing about that requires hiding the inline preview.
 * Each PlanStepTracker instance renders exactly one fixed planStatus snapshot
 * for its whole lifetime (AtomResponsePanel stacks results append-only, never
 * swapping an existing entry's planStatus for a different plan), so this
 * component's own expand/fetch state never needs to reset on prop change.
 */
function stepIcon(status) {
  switch (status) {
    case 'DONE':
      return <CheckCircleIcon sx={{ color: '#22c55e' }} fontSize="small" />;
    case 'FAILED':
      return <ErrorIcon sx={{ color: '#ef4444' }} fontSize="small" />;
    case 'BLOCKED':
      return <HourglassBottomIcon sx={{ color: '#f59e0b' }} fontSize="small" />;
    case 'SKIPPED':
      return <RemoveCircleOutlineIcon sx={{ color: '#9ca3af' }} fontSize="small" />;
    default:
      return <RadioButtonUncheckedIcon sx={{ color: '#9ca3af' }} fontSize="small" />;
  }
}

function childPlanKeyOf(item) {
  return item?.result?.value?.childPlanKey || null;
}

const PlanStepTracker = ({ planStatus, colors, depth = 0 }) => {
  // Hooks must run unconditionally before any early return, even one for a
  // nested instance that may render no expandable steps at all.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [childStatuses, setChildStatuses] = useState({}); // childPlanKey -> { loading, error, data }
  const [showSteps, setShowSteps] = useState(false);

  if (!planStatus || planStatus.hasPlan === false) {
    return null;
  }

  const items = planStatus.items || [];
  const phase = planStatus.phase || 'DRAFT';
  // activeStep = index of the first item still needing attention (PENDING,
  // FAILED, or BLOCKED -- a question awaiting the tenant's answer is exactly
  // as much "needs attention" as a failure), or the last index once
  // everything is DONE/SKIPPED.
  const activeIndex = items.findIndex(
    (i) => i.status === 'PENDING' || i.status === 'FAILED' || i.status === 'BLOCKED'
  );
  const activeStep = activeIndex >= 0 ? activeIndex : Math.max(items.length - 1, 0);

  const toggleExpand = async (childPlanKey) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(childPlanKey)) {
        next.delete(childPlanKey);
      } else {
        next.add(childPlanKey);
      }
      return next;
    });
    if (childStatuses[childPlanKey]) {
      return; // already fetched (or in flight) -- toggle is a pure show/hide from here on
    }
    setChildStatuses((prev) => ({ ...prev, [childPlanKey]: { loading: true } }));
    try {
      const response = await FlexService.executeScript({
        scriptName: 'get_plan_status',
        context: { _payload: { planKey: childPlanKey } },
      });
      const result = response?.context?.result;
      setChildStatuses((prev) => ({ ...prev, [childPlanKey]: { loading: false, data: result } }));
    } catch (e) {
      setChildStatuses((prev) => ({ ...prev, [childPlanKey]: { loading: false, error: e.message || 'load failed' } }));
    }
  };

  const expandableItems = items.filter((item) => childPlanKeyOf(item) && expandedKeys.has(childPlanKeyOf(item)));
  // FH-272: a part counted as not started while its records are being built shows as building, with its count
  const buildPhases = Array.isArray(planStatus.phases) && planStatus.phases.length > 0
    ? planStatus.phases.map((p) => (p.status === 'NOT_STARTED' && p.recordsBuilt > 0 ? { ...p, status: 'BUILDING' } : p))
    : null;
  const activePhase = buildPhases
    ? Math.max(buildPhases.findIndex((p) => p.status !== 'BUILT'), 0)
    : 0;

  return (
    <Box
      sx={{
        p: depth === 0 ? 2.5 : 1.5,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        bgcolor: depth === 0 ? colors.surface : 'transparent',
        ...(depth > 0 ? { ml: 3, mt: 1 } : {}),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {depth > 0 && <AccountTreeIcon sx={{ fontSize: 16, color: colors.textMuted }} />}
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.textPrimary }}>
            {planStatus.planTitle || 'Plan progress'}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={PHASE_LABELS[phase] || phase}
          color={PHASE_COLORS[phase] || 'default'}
          variant={phase === 'DRAFT' ? 'outlined' : 'filled'}
        />
      </Box>

      {buildPhases && (
        <>
          <Stepper activeStep={activePhase} alternativeLabel data-testid="plan-build-phases">
            {buildPhases.map((p) => (
              <Step key={p.phase} completed={p.status === 'BUILT'}>
                <StepLabel
                  error={p.status === 'FAILED'}
                  icon={buildPhaseIcon(p.status)}
                  sx={{ '& .MuiStepLabel-label': { fontSize: '0.78rem', color: colors.textSecondary, mt: 0.5 } }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box component="span" sx={{ fontWeight: 600, color: colors.textPrimary }}>{p.phase}</Box>
                    <Box component="span">{recordsText(p)}</Box>
                    <Box component="span" sx={{ color: colors.textMuted }}>{BUILD_PHASE_LABELS[p.status] || p.status}</Box>
                  </Box>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box sx={{ mt: 1 }}>
            <Typography
              component="button"
              variant="caption"
              onClick={() => setShowSteps((v) => !v)}
              sx={{ border: 0, background: 'none', p: 0, cursor: 'pointer', color: colors.textSecondary, textDecoration: 'underline' }}
            >
              {showSteps ? 'Hide the steps' : 'Show every step'}
            </Typography>
          </Box>
          <Collapse in={showSteps} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1 }}>
              {buildPhases.map((p) => (
                <Box key={p.phase} sx={{ mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: colors.textPrimary, display: 'block' }}>
                    {p.phase}
                  </Typography>
                  {items.filter((i) => i.buildPhase === p.phase).map((i) => (
                    <Box key={i.sequenceNumber ?? i.stepKey} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, ml: 1 }}>
                      {stepIcon(i.status)}
                      <Box>
                        <Typography variant="caption" sx={{ color: colors.textSecondary, display: 'block' }}>
                          {i.description}
                        </Typography>
                        {i.status === 'FAILED' && i.detail && (
                          <Typography variant="caption" sx={{ color: '#ef4444', display: 'block' }}>
                            {i.detail}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Collapse>
        </>
      )}

      {buildPhases ? null : items.length > 0 ? (
        <Stepper activeStep={activeStep} alternativeLabel>
          {items.map((item) => {
            const childPlanKey = childPlanKeyOf(item);
            const isExpanded = childPlanKey ? expandedKeys.has(childPlanKey) : false;
            const promoted = item?.result?.value?.promoted === true;
            return (
              <Step key={item.sequenceNumber ?? item.stepKey ?? item.description} completed={item.status === 'DONE'}>
                <StepLabel
                  error={item.status === 'FAILED'}
                  icon={stepIcon(item.status)}
                  sx={{
                    '& .MuiStepLabel-label': {
                      fontSize: '0.78rem',
                      color: colors.textSecondary,
                      mt: 0.5,
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <Box component="span">{item.description}</Box>
                    {childPlanKey && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        {promoted && (
                          <Chip
                            size="small"
                            label="split into its own plan"
                            variant="outlined"
                            sx={{ fontSize: '0.62rem', height: 18, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(childPlanKey)}
                          sx={{ p: 0.25 }}
                          title={isExpanded ? 'Hide sub-plan steps' : 'Show sub-plan steps'}
                        >
                          {isExpanded ? <ExpandLessIcon fontSize="inherit" /> : <ExpandMoreIcon fontSize="inherit" />}
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
      ) : (
        <Typography variant="body2" sx={{ color: colors.textMuted }}>
          No steps yet.
        </Typography>
      )}

      {/* Nested sub-plans render below the whole stepper, in step order, rather than inline
          inside a horizontal alternativeLabel step -- that layout has no per-step content slot.
          A reader matches each expanded block back to its step via the "Sub-plan for: <description>"
          caption. */}
      {expandableItems.map((item) => {
        const childPlanKey = childPlanKeyOf(item);
        const state = childStatuses[childPlanKey] || {};
        return (
          <Collapse in key={childPlanKey} timeout="auto" unmountOnExit>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" sx={{ color: colors.textMuted, display: 'block', mb: 0.5, ml: 3 }}>
                Sub-plan for: {item.description}
              </Typography>
              {state.loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 3 }}>
                  <CircularProgress size={14} />
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    Loading sub-plan…
                  </Typography>
                </Box>
              )}
              {state.error && (
                <Typography variant="caption" sx={{ color: '#ef4444', ml: 3, display: 'block' }}>
                  Could not load sub-plan: {state.error}
                </Typography>
              )}
              {state.data && <PlanStepTracker planStatus={state.data} colors={colors} depth={depth + 1} />}
            </Box>
          </Collapse>
        );
      })}

      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {planStatus.doneCount ?? 0} done
        </Typography>
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {planStatus.pendingCount ?? 0} pending
        </Typography>
        {planStatus.blockedCount > 0 && (
          <Typography variant="caption" sx={{ color: '#f59e0b' }}>
            {planStatus.blockedCount} blocked
          </Typography>
        )}
        {planStatus.failedCount > 0 && (
          <Typography variant="caption" sx={{ color: '#ef4444' }}>
            {planStatus.failedCount} failed
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default PlanStepTracker;
